import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Volume2, VolumeX, HelpCircle, Lock, Trophy } from "lucide-react";
import { api } from "../lib/api";
import { fuzzyMatch } from "../lib/match";
import { stLeadsFor, hrLabel, isPulseless } from "../lib/ekg";
import { updateMissedRhythms } from "../lib/sr";
import { recordSession } from "../lib/sessions";
import { awardCoins } from "../lib/coins";
import { useMuteHotkey } from "../lib/useMuteHotkey";
import { qrsBeep as playBeep, startAlarm, stopAlarm, setMuted, isMuted, unlockAudio, isAudioUnlocked } from "../lib/audio";
import { useProgress } from "../context/ProgressContext";
import { useModality } from "../context/ModalityContext";
import { StudyTag } from "../components/ModalitySwitch";
import { StudySummary } from "../components/StudySummary";
import { MonitorStrip } from "../components/MonitorStrip";
import { TwelveLeadGrid } from "../components/TwelveLeadGrid";

const Q_PER_LEVEL = 8;
const MODES = [
  { id: "easy", label: "Easy", desc: "Multiple choice — pick the rhythm from four options. One shot per strip." },
  { id: "hardcore", label: "Hardcore", desc: "Type the rhythm name. Two attempts per strip before the answer is revealed." },
];


export default function EkgTrainer() {
  const { progress, update } = useProgress();
  const { isStudy } = useModality();
  const [levels, setLevels] = useState([]);
  const [stage, setStage] = useState("map"); // map | playing | studySummary
  const [activeLevel, setActiveLevel] = useState(null);
  const [mode, setMode] = useState("hardcore"); // easy | hardcore
  const [studyResult, setStudyResult] = useState(null);
  const [runStudy, setRunStudy] = useState(false); // modality frozen at level start

  useEffect(() => {
    api.get("/rhythms/levels").then((r) => setLevels(r.data));
  }, []);

  const ekgProg = progress?.ekg || {};

  const isUnlocked = (lvl) => {
    if (lvl.level === 1 || isStudy) return true;
    return !!ekgProg[lvl.level - 1]?.cleared;
  };

  if (stage === "studySummary" && studyResult) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-10">
        <StudySummary
          kicker={`LEVEL ${activeLevel.level} · ${activeLevel.name.toUpperCase()} · ${mode.toUpperCase()}`}
          attempted={studyResult.attempted}
          missed={studyResult.missed.map((r) => ({ id: r.id, prompt: `Strip · ${hrLabel(r.params)} bpm · ${r.track === "twelve" ? "12-lead" : "Lead II"}`, answer: r.name, detail: r.explanation }))}
          onRestart={() => setStage("playing")}
          restartLabel="Replay level"
          testid="ekg-study-summary"
        />
        <button onClick={() => setStage("map")} data-testid="ekg-study-back-map" className="mt-4 text-xs tracking-widest text-muted-foreground hover:text-foreground">← BACK TO MAP</button>
      </div>
    );
  }

  if (stage === "playing" && activeLevel) {
    return (
      <LevelRunner
        level={activeLevel}
        mode={mode}
        study={runStudy}
        onExit={() => setStage("map")}
        onComplete={(score, cleared, outcome) => {
          if (runStudy) {
            const stats = awardCoins(progress?.stats || {}, score, "EKG study");
            update({ stats: { ...stats, ekg_study_sessions: (stats.ekg_study_sessions || 0) + 1, missed_rhythms: updateMissedRhythms(stats, outcome.missed, outcome.correct) } });
            setStudyResult({ missed: outcome.missedItems, attempted: outcome.attempted });
            setStage("studySummary");
            return;
          }
          recordSession({ kind: "ekg", category: `ekg_${activeLevel.level}`, mode, score });
          const nextEkg = { ...(progress.ekg || {}) };
          const prevBest = nextEkg[activeLevel.level]?.best_score || 0;
          const prev = nextEkg[activeLevel.level] || {};
          nextEkg[activeLevel.level] = {
            ...prev,
            best_score: Math.max(prevBest, score),
            cleared: cleared || !!prev.cleared,
            attempts: (prev.attempts || 0) + 1,
            [`best_${mode}`]: Math.max(prev[`best_${mode}`] || 0, score),
            [`cleared_${mode}`]: cleared || !!prev[`cleared_${mode}`],
          };
          const stats = awardCoins(progress?.stats || {}, score, "EKG level");
          update({
            ekg: nextEkg,
            stats: {
              ...stats,
              ekg_highest_level: Math.max(stats.ekg_highest_level || 1, cleared ? activeLevel.level + 1 : activeLevel.level),
              streak: (stats.streak || 0) + (cleared ? 1 : 0),
              missed_rhythms: updateMissedRhythms(stats, outcome.missed, outcome.correct),
            },
          });
          setStage("map");
        }}
      />
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-xs tracking-widest text-muted-foreground hover:text-foreground mb-6" data-testid="back-dashboard">
        <ArrowLeft className="w-3 h-3" /> BACK
      </Link>

      <div className="mb-10">
        <div className="text-[11px] tracking-[0.3em] text-muted-foreground mb-2">// EKG TRAINER{isStudy && <StudyTag testid="ekg-map-study-tag" />}</div>
        <h1 className="text-4xl font-extrabold tracking-tight">
          Rhythm <span className="text-phosphor">progression</span> map
        </h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
          {isStudy
            ? "Study mode: every level is open, nothing is scored. Miss a strip and the answer plus explanation appear — lock it in and keep going."
            : "Clear each level by reaching the target score. Levels gate the next until you prove them."}
        </p>
      </div>

      <div className="panel p-6 mb-6">
        <div className="text-[10px] tracking-[0.3em] text-muted-foreground mb-4">MODE</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => setMode(m.id)}
              data-testid={`ekg-mode-${m.id}`}
              className={`text-left px-4 py-3 border transition-colors ${
                mode === m.id
                  ? m.id === "hardcore" ? "border-destructive/70 bg-destructive/10 text-destructive" : "border-phosphor bg-primary/10 text-phosphor"
                  : "border-border hover:border-phosphor/60"
              }`}
            >
              <div className="text-sm font-semibold tracking-widest uppercase">{m.label}</div>
              <div className="text-xs text-muted-foreground mt-1">{m.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {levels.map((lvl, i) => {
          const unlocked = isUnlocked(lvl);
          const data = ekgProg[lvl.level];
          const cleared = !!data?.cleared;
          return (
            <div
              key={lvl.level}
              data-testid={`level-row-${lvl.level}`}
              className={`panel p-5 flex flex-col sm:flex-row sm:items-center gap-4 ${!unlocked ? "opacity-50" : ""}`}
            >
              <div className="w-12 h-12 flex items-center justify-center border border-phosphor/30 text-phosphor font-bold text-lg">
                {lvl.level}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-bold">{lvl.name}</h3>
                  {cleared && <Trophy className="w-4 h-4 text-phosphor" />}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Target {lvl.target_score} · Best {data?.best_score ?? 0}
                </div>
              </div>
              {unlocked ? (
                <button
                  onClick={() => { setActiveLevel(lvl); setRunStudy(isStudy); setStage("playing"); }}
                  data-testid={`play-level-${lvl.level}`}
                  className="bg-primary text-primary-foreground px-6 py-2 text-xs font-semibold tracking-[0.25em] uppercase hover:bg-primary/90"
                >
                  {cleared ? "Replay" : "Start"}
                </button>
              ) : (
                <div className="flex items-center gap-2 text-xs text-muted-foreground tracking-widest">
                  <Lock className="w-3 h-3" /> LOCKED
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function buildOptions(current, pool) {
  const sameLevel = pool.filter((r) => r.id !== current.id && r.level === current.level).sort(() => Math.random() - 0.5);
  const others = pool.filter((r) => r.id !== current.id && r.level !== current.level).sort(() => Math.random() - 0.5);
  return [current, ...[...sameLevel, ...others].slice(0, 3)].sort(() => Math.random() - 0.5);
}

function LevelRunner({ level, mode, study = false, onExit, onComplete }) {
  const isEasy = mode === "easy";
  const [allRhythms, setAllRhythms] = useState([]);
  const [picked, setPicked] = useState(null);
  const [queue, setQueue] = useState([]);
  const [idx, setIdx] = useState(0);
  const [guess, setGuess] = useState("");
  const [attempts, setAttempts] = useState(0);
  const [reveal, setReveal] = useState(false);
  const [score, setScore] = useState(0);
  const [mutedState, setMutedState] = useState(isMuted());
  const [audioOk, setAudioOk] = useState(isAudioUnlocked());
  const [feedback, setFeedback] = useState(null);
  const outcomeRef = useRef({ missed: [], correct: [], missedItems: [], attempted: 0 });
  const advancingRef = useRef(false);
  useMuteHotkey(setMutedState);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/rhythms").then((r) => setAllRhythms(r.data));
  }, []);

  useEffect(() => {
    api.get("/rhythms", { params: { level: level.level } }).then((r) => {
      const base = [...r.data].sort(() => Math.random() - 0.5);
      const q = [];
      let i = 0;
      while (q.length < Math.max(Q_PER_LEVEL, base.length)) {
        q.push(base[i % base.length]);
        i++;
      }
      setQueue(q);
    });
    return () => stopAlarm();
  }, [level.level]);

  const current = queue[idx];
  const isTwelve = current?.track === "twelve";
  const options = useMemo(
    () => (current && isEasy && allRhythms.length ? buildOptions(current, allRhythms) : []),
    [current, isEasy, allRhythms]
  );

  const markCorrect = () => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    outcomeRef.current.correct.push(current.id);
    outcomeRef.current.attempted += 1;
    setFeedback({ ok: true, text: study ? `Correct • ${current.name}` : `+15 • ${current.name}` });
    setTimeout(() => nextQuestion(15), 650);
  };

  const markMissed = (text) => {
    outcomeRef.current.missed.push(current.id);
    outcomeRef.current.missedItems.push(current);
    outcomeRef.current.attempted += 1;
    setReveal(true);
    setFeedback({ ok: false, text });
  };

  const lockIn = () => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    setFeedback({ ok: true, text: `Locked in • ${current.name}` });
    setTimeout(() => nextQuestion(0), 650);
  };

  const choose = (opt) => {
    if (!current || advancingRef.current) return;
    if (reveal) {
      if (study && opt.id === current.id && !picked?.startsWith("locked")) { setPicked(`locked:${opt.id}`); lockIn(); }
      return;
    }
    if (picked) return;
    setPicked(opt.id);
    if (opt.id === current.id) markCorrect();
    else markMissed(study ? `Not quite — it's ${current.name}. Tap it to lock it in.` : `Answer: ${current.name}`);
  };

  useEffect(() => {
    if (!isEasy) return;
    const onKey = (e) => {
      const n = Number(e.key);
      if (n >= 1 && n <= options.length) choose(options[n - 1]);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEasy, options, picked, reveal]);

  useEffect(() => {
    if (!current) return;
    stopAlarm();
    if (current.lethal && (current.params.pattern === "asystole" || current.params.pattern === "vfib")) startAlarm();
    return () => stopAlarm();
  }, [current]);

  const nextQuestion = useCallback((bump) => {
    const nextIdx = idx + 1;
    const nextScore = score + bump;
    setScore(nextScore);
    setGuess("");
    setAttempts(0);
    setReveal(false);
    setFeedback(null);
    setPicked(null);
    advancingRef.current = false;
    stopAlarm();
    if (nextIdx >= queue.length) {
      const cleared = nextScore >= level.target_score;
      onComplete(nextScore, cleared, outcomeRef.current);
    } else {
      setIdx(nextIdx);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [idx, score, queue.length, level.target_score, onComplete]);

  const submit = (e) => {
    e.preventDefault();
    if (!current || advancingRef.current) return;
    if (reveal) {
      if (study && guess.trim() && fuzzyMatch(guess, current.aliases)) { setGuess(""); lockIn(); }
      else if (study && guess.trim()) { setFeedback({ ok: false, text: `Not quite — it's ${current.name}. Type it to lock it in.` }); setGuess(""); }
      return;
    }
    if (fuzzyMatch(guess, current.aliases)) {
      markCorrect();
    } else if (study) {
      markMissed(`Not quite — it's ${current.name}. Type it to lock it in.`);
      setGuess("");
    } else {
      const n = attempts + 1;
      setAttempts(n);
      if (n >= 2) {
        markMissed(`Answer: ${current.name}`);
      } else {
        setFeedback({ ok: false, text: `Not quite. Try again (${2 - n} left)` });
        setGuess("");
      }
    }
  };

  const giveUp = () => {
    if (reveal) return;
    markMissed(study ? `It's ${current.name}. Type it to lock it in.` : `Answer: ${current.name}`);
  };

  const handleMute = () => {
    const m = !mutedState;
    setMuted(m);
    setMutedState(m);
    if (m) stopAlarm();
  };

  const handleUnlockAudio = async () => {
    const ok = await unlockAudio();
    setAudioOk(ok);
  };

  if (!current) {
    return <div className="max-w-6xl mx-auto px-6 py-10 text-muted-foreground">Loading rhythms…</div>;
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between mb-6">
        <button onClick={onExit} className="inline-flex items-center gap-2 text-xs tracking-widest text-muted-foreground hover:text-foreground" data-testid="exit-level">
          <ArrowLeft className="w-3 h-3" /> EXIT LEVEL
        </button>
        <div className="flex items-center gap-6">
          <div>
            <div className="text-[10px] tracking-widest text-muted-foreground">
              LEVEL {level.level}
              <span className={`ml-2 px-1.5 py-0.5 border text-[9px] ${isEasy ? "border-phosphor/50 text-phosphor" : "border-destructive/50 text-destructive"}`} data-testid="ekg-mode-tag">
                {isEasy ? "EASY" : "HARDCORE"}
              </span>
              {study && <StudyTag testid="ekg-study-tag" />}
            </div>
            <div className="text-sm font-semibold">{level.name}</div>
          </div>
          <div data-testid="ekg-hr">
            <div className="text-[10px] tracking-widest text-muted-foreground">PULSE</div>
            <div className={`text-xl font-bold tabular-nums ${isPulseless(current.params) ? "text-destructive" : "text-phosphor"}`}>
              {hrLabel(current.params)} <span className="text-xs text-muted-foreground">bpm</span>
            </div>
          </div>
          {study ? (
            <div data-testid="ekg-missed-count">
              <div className="text-[10px] tracking-widest text-muted-foreground">TO REVIEW</div>
              <div className={`text-xl font-bold ${outcomeRef.current.missed.length ? "text-destructive" : ""}`}>{outcomeRef.current.missed.length}</div>
            </div>
          ) : (
          <div data-testid="ekg-score">
            <div className="text-[10px] tracking-widest text-muted-foreground">SCORE</div>
            <div className="text-xl font-bold text-phosphor">{score} <span className="text-xs text-muted-foreground">/ {level.target_score}</span></div>
          </div>
          )}
          <div>
            <div className="text-[10px] tracking-widest text-muted-foreground">STRIP</div>
            <div className="text-xl font-bold">{idx + 1}<span className="text-xs text-muted-foreground"> / {queue.length}</span></div>
          </div>
          <button onClick={handleMute} data-testid="mute-toggle" title="Toggle monitor audio (M)" aria-label="Toggle monitor audio, hotkey M" className="relative p-2 border border-phosphor/30 hover:border-phosphor transition-colors">
            <span className="absolute -top-2 -right-2 text-[8px] px-1 border border-border bg-background text-muted-foreground" aria-hidden>M</span>
            {mutedState ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-phosphor" />}
          </button>
        </div>
      </div>

      {!audioOk && (
        <button
          onClick={handleUnlockAudio}
          data-testid="enable-audio-btn"
          className="w-full mb-3 py-2 border border-phosphor/40 text-xs tracking-widest text-phosphor hover:bg-primary/10"
        >
          TAP TO ENABLE MONITOR AUDIO
        </button>
      )}

      <div className="panel scanlines p-2" data-testid="ekg-canvas-wrap">
        {isTwelve ? (
          <TwelveLeadGrid params={current.params} onBeat={playBeep} highlight={reveal ? stLeadsFor(current.params.pattern) : []} />
        ) : (
          <MonitorStrip params={current.params} onBeat={playBeep} />
        )}
      </div>

      <div className="text-[10px] tracking-[0.3em] text-muted-foreground mt-2 flex flex-wrap items-center gap-3" data-testid="ekg-lead-label">
        <span>{isTwelve ? "12-LEAD · 4×3 GRID + LEAD II RHYTHM STRIP" : "LEAD II"}</span>
        {current.lethal && <span className="text-destructive">● LETHAL</span>}
        {isTwelve && reveal && stLeadsFor(current.params.pattern).length > 0 && (
          <span className="text-destructive" data-testid="ekg-st-leads">
            ST ELEVATION: {stLeadsFor(current.params.pattern).join(", ")}
          </span>
        )}
      </div>

      {isEasy ? (
        <div className="mt-5 grid sm:grid-cols-2 gap-3" data-testid="ekg-options">
          {options.map((opt, i) => {
            const show = picked !== null;
            const isRight = opt.id === current.id;
            const canLock = study && reveal && isRight && !picked?.startsWith("locked");
            const cls = !show
              ? "border-phosphor/30 hover:border-phosphor hover:bg-primary/10"
              : isRight
                ? `border-phosphor bg-primary/10 text-phosphor ${canLock ? "pulse-glow" : ""}`
                : picked === opt.id
                  ? "border-destructive bg-destructive/10 text-destructive"
                  : "border-border opacity-40";
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => choose(opt)}
                disabled={show && !canLock}
                data-testid={`ekg-option-${i}`}
                data-option-id={opt.id}
                className={`text-left px-4 py-3 border transition-colors flex items-center gap-3 ${cls}`}
              >
                <span className="w-6 h-6 shrink-0 flex items-center justify-center border border-current text-[10px] font-bold">{i + 1}</span>
                <span className="text-sm font-semibold">{opt.name}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <form onSubmit={submit} className="mt-5 flex gap-3" data-testid="ekg-form">
          <input
            ref={inputRef}
            value={guess}
            onChange={(e) => setGuess(e.target.value)}
            placeholder='Type the rhythm (e.g. "afib")'
            data-testid="ekg-input"
            className="flex-1 bg-transparent border border-phosphor/30 focus:border-phosphor focus:ring-1 focus:ring-primary outline-none px-4 py-3 text-sm font-mono"
            autoComplete="off"
            disabled={reveal && !study}
          />
          <button type="submit" disabled={reveal && !study} data-testid="ekg-submit" className="bg-primary text-primary-foreground px-6 py-3 text-xs tracking-[0.3em] font-semibold hover:bg-primary/90 disabled:opacity-50">
            ENTER
          </button>
          <button type="button" onClick={giveUp} disabled={reveal} data-testid="ekg-dont-know" className="border border-destructive/50 text-destructive px-4 py-3 text-xs tracking-widest hover:bg-destructive/10 disabled:opacity-50">
            <HelpCircle className="w-4 h-4 inline mr-1" /> I don&apos;t know
          </button>
        </form>
      )}

      {feedback && (
        <div data-testid="ekg-feedback" className={`mt-4 p-4 border ${feedback.ok ? "border-phosphor text-phosphor" : "border-destructive text-destructive"}`}>
          <div className="text-sm font-semibold">{feedback.text}</div>
          {reveal && (
            <>
              <div className="text-xs text-muted-foreground mt-2">{current.explanation}</div>
              <button onClick={() => { if (!advancingRef.current) nextQuestion(0); }} data-testid="ekg-continue" className="mt-4 bg-primary text-primary-foreground px-5 py-2 text-xs tracking-widest font-semibold hover:bg-primary/90">
                {study ? "SKIP →" : "CONTINUE →"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
